import React, { createContext, useState, useContext, useEffect } from 'react';
import { createTiles, shuffleTiles, TILE_TYPES } from '../utils/tileUtils';

const GameContext = createContext();

export const GameProvider = ({ children }) => {
  const [gameState, setGameState] = useState({
    isPlaying: false,
    tiles: [],
    playerTiles: [],
    computerTiles: [],
    discardPile: [],
    currentPlayer: 'player',
    score: 0,
    remainingTiles: 0,
    selectedTile: null,
  });

  // 로컬 스토리지에서 최고 점수 가져오기
  const [highScore, setHighScore] = useState(() => {
    const savedHighScore = localStorage.getItem('highScore');
    return savedHighScore ? parseInt(savedHighScore) : 0;
  });

  // 게임 시작 함수
  const startGame = () => {
    // 타일 생성 및 섞기
    const allTiles = shuffleTiles(createTiles());
    
    // 플레이어와 컴퓨터에게 타일 분배
    const playerTiles = allTiles.slice(0, 13);
    const computerTiles = allTiles.slice(13, 26);
    const remainingTiles = allTiles.slice(26);
    
    setGameState({
      isPlaying: true,
      tiles: remainingTiles,
      playerTiles,
      computerTiles,
      discardPile: [],
      currentPlayer: 'player',
      score: 0,
      remainingTiles: remainingTiles.length,
      selectedTile: null,
    });
  };

  // 게임 재설정 함수
  const resetGame = () => {
    setGameState({
      isPlaying: false,
      tiles: [],
      playerTiles: [],
      computerTiles: [],
      discardPile: [],
      currentPlayer: 'player',
      score: 0,
      remainingTiles: 0,
      selectedTile: null,
    });
  };

  // 타일 선택 함수
  const selectTile = (tile) => {
    setGameState({
      ...gameState,
      selectedTile: tile,
    });
  };

  // 타일 버리기 함수
  const discardTile = (tile) => {
    if (!tile) return;
    
    // 플레이어 타일에서 선택한 타일 제거
    const updatedPlayerTiles = gameState.playerTiles.filter(t => t.id !== tile.id);
    
    // 버린 패에 타일 추가
    const updatedDiscardPile = [...gameState.discardPile, tile];
    
    // 게임 상태 업데이트
    setGameState({
      ...gameState,
      playerTiles: updatedPlayerTiles,
      discardPile: updatedDiscardPile,
      selectedTile: null,
      currentPlayer: 'computer', // 컴퓨터 차례로 변경
    });
    
    // 컴퓨터 차례 진행
    setTimeout(computerTurn, 1000);
  };

  // 타일 가져오기 함수
  const drawTile = () => {
    if (gameState.tiles.length === 0) return;
    
    // 남은 타일에서 하나 가져오기
    const drawnTile = gameState.tiles[0];
    const updatedTiles = gameState.tiles.slice(1);
    
    // 플레이어 타일에 추가
    const updatedPlayerTiles = [...gameState.playerTiles, drawnTile];
    
    // 게임 상태 업데이트
    setGameState({
      ...gameState,
      tiles: updatedTiles,
      playerTiles: updatedPlayerTiles,
      remainingTiles: updatedTiles.length,
    });
    
    // 패턴 체크 및 점수 계산
    checkPatterns(updatedPlayerTiles);
  };

  // 컴퓨터 차례 함수
  const computerTurn = () => {
    if (gameState.tiles.length === 0) {
      endGame();
      return;
    }
    
    // 컴퓨터가 타일 가져오기
    const drawnTile = gameState.tiles[0];
    const updatedTiles = gameState.tiles.slice(1);
    
    // 컴퓨터 타일에 추가
    const updatedComputerTiles = [...gameState.computerTiles, drawnTile];
    
    // 컴퓨터가 타일 버리기 (간단한 AI: 랜덤하게 타일 선택)
    const randomIndex = Math.floor(Math.random() * updatedComputerTiles.length);
    const discardedTile = updatedComputerTiles[randomIndex];
    const finalComputerTiles = updatedComputerTiles.filter((_, index) => index !== randomIndex);
    
    // 버린 패에 타일 추가
    const updatedDiscardPile = [...gameState.discardPile, discardedTile];
    
    // 게임 상태 업데이트
    setGameState({
      ...gameState,
      tiles: updatedTiles,
      computerTiles: finalComputerTiles,
      discardPile: updatedDiscardPile,
      currentPlayer: 'player', // 플레이어 차례로 변경
      remainingTiles: updatedTiles.length,
    });
  };

  // 패턴 체크 및 점수 계산 함수
  const checkPatterns = (tiles) => {
    let additionalScore = 0;
    
    // 같은 종류의 타일 3개 체크 (예: 같은 숫자의 원 타일 3개)
    const typeCounts = {};
    
    tiles.forEach(tile => {
      const key = `${tile.type}-${tile.value}`;
      typeCounts[key] = (typeCounts[key] || 0) + 1;
      
      // 같은 종류의 타일이 3개 이상이면 점수 추가
      if (typeCounts[key] === 3) {
        additionalScore += 10;
      }
    });
    
    // 연속된 숫자 체크 (예: 1-2-3 원 타일)
    const dotTiles = tiles.filter(tile => tile.type === TILE_TYPES.DOT);
    const bambooTiles = tiles.filter(tile => tile.type === TILE_TYPES.BAMBOO);
    const characterTiles = tiles.filter(tile => tile.type === TILE_TYPES.CHARACTER);
    
    // 각 타입별로 연속된 숫자 체크
    additionalScore += checkSequence(dotTiles);
    additionalScore += checkSequence(bambooTiles);
    additionalScore += checkSequence(characterTiles);
    
    // 점수 업데이트
    if (additionalScore > 0) {
      const newScore = gameState.score + additionalScore;
      setGameState({
        ...gameState,
        score: newScore,
      });
      
      // 최고 점수 업데이트
      if (newScore > highScore) {
        setHighScore(newScore);
        localStorage.setItem('highScore', newScore.toString());
      }
    }
  };

  // 연속된 숫자 체크 함수
  const checkSequence = (tiles) => {
    if (tiles.length < 3) return 0;
    
    // 값으로 정렬
    const sortedTiles = [...tiles].sort((a, b) => a.value - b.value);
    let score = 0;
    
    // 연속된 3개 숫자 체크
    for (let i = 0; i < sortedTiles.length - 2; i++) {
      if (
        sortedTiles[i].value + 1 === sortedTiles[i + 1].value &&
        sortedTiles[i + 1].value + 1 === sortedTiles[i + 2].value
      ) {
        score += 15; // 연속된 숫자는 더 높은 점수
      }
    }
    
    return score;
  };

  // 게임 종료 함수
  const endGame = () => {
    // 최종 점수 계산
    const finalScore = gameState.score;
    
    // 최고 점수 업데이트
    if (finalScore > highScore) {
      setHighScore(finalScore);
      localStorage.setItem('highScore', finalScore.toString());
    }
    
    // 게임 종료 상태로 변경
    setGameState({
      ...gameState,
      isPlaying: false,
    });
    
    // 게임 종료 알림
    alert(`게임 종료! 최종 점수: ${finalScore}`);
  };

  return (
    <GameContext.Provider 
      value={{ 
        gameState, 
        highScore,
        startGame, 
        resetGame, 
        selectTile,
        discardTile,
        drawTile,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => useContext(GameContext);
